import { skipToken } from "@reduxjs/toolkit/query";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery } from "../../store/api/authApi";
import type { UserRole } from "../../types";
import AdminLayout from "../layouts/AdminLayout";
import UserLayout from "../layouts/UserLayout";
import Loader from "../ui/Loader";

interface ProtectedRouteProps {
	children: React.ReactNode;
	allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
	children,
	allowedRoles,
}) => {
	const location = useLocation();
	const accessToken = tokenStorage.getAccessToken();

	// If no token, immediately redirect to login
	if (!accessToken) {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	// Only fetch profile if we have a token (skip query if no token)
	const {
		data: user,
		isLoading,
		isError,
	} = useGetProfileQuery(accessToken ? undefined : skipToken);

	// Show loading state while checking authentication
	if (isLoading) {
		return <Loader fullScreen showText text="Loading..." />;
	}

	// Redirect to login if profile fetch failed or user is null
	if (isError || !user) {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	// Normalize role to ensure it's a string (backend returns enum values as strings)
	const userRole =
		typeof user.role === "string"
			? user.role.toLowerCase()
			: String(user.role).toLowerCase();

	// Debug logging (only in development with explicit flag)
	// Set window.DEBUG_PROTECTED_ROUTE = true in console to enable
	if (
		process.env.NODE_ENV === "development" &&
		typeof window !== "undefined" &&
		(window as any).DEBUG_PROTECTED_ROUTE
	) {
		const isAdmin = userRole === "admin";
		const hasDirectAccess = allowedRoles
			? allowedRoles.includes(userRole as UserRole)
			: true;
		const hasAccess = isAdmin || hasDirectAccess;
		// Debug logging available when DEBUG_PROTECTED_ROUTE flag is set
	}

	// Check if user role is allowed
	// Admins can access all pages (for management/testing purposes)
	if (
		allowedRoles &&
		allowedRoles.length > 0 &&
		userRole !== "admin" &&
		!allowedRoles.includes(userRole as UserRole)
	) {
		// User doesn't have access to this route - redirect to their dashboard
		switch (userRole) {
			case "admin":
				return <Navigate to="/admin-dashboard" replace />;
			case "tenant":
				return <Navigate to="/tenant-dashboard" replace />;
			case "technician":
				return <Navigate to="/technician-dashboard" replace />;
			case "agent":
				return <Navigate to="/agent-dashboard" replace />;
			default:
				return <Navigate to="/" replace />;
		}
	}

	// Render with appropriate layout based on role
	if (userRole === "admin") {
		return <AdminLayout>{children}</AdminLayout>;
	}

	// Non-admin users (tenant, technician, agent) use UserLayout
	return <UserLayout>{children}</UserLayout>;
};

export default ProtectedRoute;
